import {
	Box,
	Col,
	Container,
	Divider,
	Grid,
	Heading,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Breadcrumbs, ButtonLink, Link } from "@sembark-travel/ui-router"
import { Helmet } from "react-helmet-async"
import { generatePath } from "../../../router-utils"
import { PlatformIcon, type TIntegration } from "../../../Integrations"
import { useHasFeatureFlag } from "../../../Auth"

export default function NewIntegrationPage() {
	const canIntegrateFB = useHasFeatureFlag("fb_app_integration")
	const canIntegratePG = useHasFeatureFlag("payment_gateway")
	const canIntegratePGPayU = useHasFeatureFlag("payment_gateway_payu")
	const canIntegratePGSimulator = useHasFeatureFlag("payment_gateway_simulator")
	const canIntegrateMGWhatsappCloudAPI = useHasFeatureFlag("whatsapp_cloud_api")
	const canIntegrateMGSimulator = useHasFeatureFlag("whatsapp_simulator")
	const canIntegrateEmail = useHasFeatureFlag("email_integration")
	const canIntegrateOAuth = useHasFeatureFlag("oauth_apps")
	const canIntegrateMG =
		canIntegrateMGWhatsappCloudAPI || canIntegrateMGSimulator
	const canIntegrateTelecalling = useHasFeatureFlag("telecalling")

	return (
		<>
			<Helmet>
				<title>New Integration</title>
			</Helmet>
			<Breadcrumbs
				title="New Integration"
				items={[
					[generatePath("/org/integrations"), "Integrations"],
					["", "New"],
				]}
			/>
			<Box paddingX={{ md: "4" }} paddingY={{ md: "8" }}>
				<Box maxWidth="3xl" marginX="auto">
					<Box borderWidth={{ md: "1" }} rounded="md" bgColor="default">
						<Container paddingY="6">
							<Stack gap="4">
								<Stack>
									<Heading as="h2">Lead Platform Integrations</Heading>
									<Text color="muted">
										Use the official integrations to bring leads from following
										Ads platforms.
									</Text>
								</Stack>
								<PlatformIntegations
									integrations={[
										{
											url: generatePath("/integrations/google-ads/new"),
											platform: "google_ads" as TIntegration["platform"],
											title: "Google Ads Lead Form",
											description: "Integrate Lead Form on Ads",
										},
									].concat(
										canIntegrateFB
											? [
													{
														url: generatePath("/integrations/meta/new"),
														platform: "meta" as const,
														title: "Meta (FB/Insta) Page Leads",
														description: "Bring leads from FB/Insta Pages",
													},
												]
											: []
									)}
								/>
							</Stack>
							<Divider />
							<Stack gap="4">
								<Stack>
									<Heading as="h2">Custom API Integrations</Heading>
									<Text color="muted">
										Use the APIs to integrate with any platform such as Website,
										Landing Pages, ChatBots etc.
									</Text>
								</Stack>
								<PlatformIntegations
									integrations={[
										{
											url: generatePath("/integrations/api/new"),
											platform: "api",
											title: "API Integration",
											description:
												"Integrate with Landing Page, ChatBots using API and Tokens",
										},
									]}
								/>
							</Stack>
							{canIntegratePG ? (
								<>
									<Divider />
									<Stack gap="4">
										<Stack>
											<Heading as="h2">Payment Solutions</Heading>
											<Text color="muted">
												Collect and Manage Payments by directly integrating your
												Payment Gateway Accounts.
											</Text>
										</Stack>
										<PlatformIntegations
											integrations={([] as Array<TPlatformIntegrationItem>)
												.concat(
													canIntegratePGPayU
														? [
																{
																	url: generatePath("/integrations/payu/new"),
																	platform: "pg_payu" as const,
																	title: "PayU Payment Gateway",
																	description: "Collect and Manage Payments",
																},
															]
														: []
												)
												.concat(
													canIntegratePGSimulator
														? [
																{
																	url: generatePath(
																		"/integrations/pg-simulator/new"
																	),
																	platform: "pg_simulator" as const,
																	title: "Payment Gateway Simulator",
																	description:
																		"Collect and Manage Payments using Gateway Simulator",
																},
															]
														: []
												)}
										/>
									</Stack>
								</>
							) : null}

							{canIntegrateMG ? (
								<>
									<Divider />
									<Stack gap="4">
										<Stack>
											<Heading as="h2">Messaging Solutions</Heading>
											<Text color="muted">
												Automatically send whatsapp messages to customers by
												integrating your Messaging Platform Accounts.
											</Text>
										</Stack>
										<PlatformIntegations
											integrations={([] as Array<TPlatformIntegrationItem>)
												.concat(
													canIntegrateMGWhatsappCloudAPI
														? [
																{
																	url: generatePath(
																		"/integrations/whatsapp-cloud-api/new"
																	),
																	platform: "whatsapp_cloud_api" as const,
																	title: "Whatsapp Cloud API",
																	description:
																		"Automate customer messages via WhatsApp.",
																},
															]
														: []
												)
												.concat(
													canIntegrateMGSimulator
														? [
																{
																	url: generatePath(
																		"/integrations/whatsapp-simulator/new"
																	),
																	platform: "whatsapp_simulator" as const,
																	title: "Whatsapp Simulator",
																	description:
																		"Automate customer messages via Simulator.",
																},
															]
														: []
												)}
										/>
									</Stack>
								</>
							) : null}
							{canIntegrateOAuth ? (
								<>
									<Divider />
									<Stack gap="4">
										<Stack>
											<Heading as="h2">OAuth Apps</Heading>
											<Text color="muted">
												Let an external application, such as Claude, ChatGPT or
												your own software, work with Sembark on behalf of your
												team members. Each member approves it once with their
												own Sembark login.
											</Text>
										</Stack>
										<PlatformIntegations
											integrations={[
												{
													url: generatePath("/integrations/oauth/new"),
													platform: "oauth" as const,
													title: "OAuth App",
													description:
														"Any OAuth 2.1 client, with presets for Claude and ChatGPT",
												},
											]}
										/>
									</Stack>
								</>
							) : null}
							{canIntegrateEmail ? (
								<>
									<Divider />
									<Stack gap="4">
										<Stack>
											<Heading as="h2">Email Mailboxes</Heading>
											<Text color="muted">
												Connect shared inboxes (Gmail, IMAP/SMTP) to manage
												incoming and outgoing email workflows.
											</Text>
										</Stack>
										<PlatformIntegations
											integrations={[
												{
													url: generatePath("/integrations/email-mailbox/new"),
													platform: "email_mailbox" as const,
													title: "Email Mailbox",
													description:
														"Connect Gmail, IMAP and SMTP mailboxes for inbox and send workflows",
												},
											]}
										/>
									</Stack>
								</>
							) : null}
							{canIntegrateTelecalling ? (
								<>
									<Divider />
									<Stack gap="4">
										<Stack>
											<Heading as="h2">Telecalling integrations</Heading>
											<Text color="muted">
												Connect your telecalling provider to automatically log
												calls and create leads in Sembark.
											</Text>
										</Stack>
										<PlatformIntegations
											integrations={[
												// {
												// 	url: generatePath("/integrations/knowlarity/new"),
												// 	platform: "tc_knowlarity",
												// 	title: "Knowlarity",
												// 	description: "Inbound call via Knowlarity",
												// },
												// {
												// 	url: generatePath("/integrations/callerdesk/new"),
												// 	platform: "tc_callerdesk",
												// 	title: "Caller Desk",
												// 	description: "Inbound call via Caller Desk",
												// },
												{
													url: generatePath("/integrations/tatatele/new"),
													platform: "tc_tatatele",
													title: "Tata Tele Business",
													description: "Inbound call via Tata Tele Business",
												},
											]}
										/>
									</Stack>
								</>
							) : null}
							<Divider />
							<ButtonLink to={generatePath("/org/integrations")} replace>
								Cancel
							</ButtonLink>
						</Container>
					</Box>
				</Box>
			</Box>
		</>
	)
}

type TPlatformIntegrationItem = {
	url: string
	platform: TIntegration["platform"]
	title: string
	description: string
}

function PlatformIntegations({
	integrations,
}: {
	integrations: Array<TPlatformIntegrationItem>
}) {
	return (
		<Grid gap="4">
			{integrations.map(({ url, platform, title, description }, index) => (
				<Col
					key={platform}
					xs={12}
					sm={index % 2 === 0 && integrations.length > 1 ? 6 : true}
				>
					<Link
						height="full"
						to={url}
						padding="4"
						borderWidth="1"
						rounded="md"
						display="block"
						backgroundColor={{ hover: "subtle" }}
					>
						<Inline gap="4" alignItems="center">
							<Box>
								<PlatformIcon size="48" platform={platform} />
							</Box>
							<Stack flex="1">
								<Heading as="h3" fontSize="md">
									{title}
								</Heading>
								<Text color="muted">{description}</Text>
							</Stack>
						</Inline>
					</Link>
				</Col>
			))}
		</Grid>
	)
}
